/**
 * usePermissions Hook
 * Core permission and feature flag hooks for RBAC
 */

import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ReactNode, Fragment, createElement } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/providers/AuthProvider'
import type { UserPermissions } from '@/types/database.types'

// Query keys
export const permissionKeys = {
  userPermissions: (userId: string) => ['userPermissions', userId] as const,
}

/**
 * Fetches and caches the current user's permissions from the get_user_permissions RPC.
 * Returns role info, permissions array, and feature flags.
 */
export function useUserPermissions() {
  const { user } = useAuth()

  return useQuery({
    queryKey: permissionKeys.userPermissions(user?.id ?? ''),
    queryFn: async () => {
      if (!user?.id) throw new Error('No user ID')

      const { data, error } = await supabase
        .rpc('get_user_permissions', { p_user_id: user.id })
        .single()

      if (error) throw error
      return data as unknown as UserPermissions
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Checks if the current user has a specific permission (resource + action).
 * Uses cached permissions from useUserPermissions — does NOT make a new RPC call.
 */
export function useHasPermission(resource: string, action: string): boolean {
  const { data: permissions } = useUserPermissions()

  if (!permissions?.permissions) return false

  return permissions.permissions.some(
    (p) => p.resource === resource && p.action === action
  )
}

/**
 * Checks if the current user has any permission on a given resource.
 * Uses cached permissions — does NOT make a new RPC call.
 */
export function useHasAnyPermission(resource: string): boolean {
  const { data: permissions } = useUserPermissions()

  if (!permissions?.permissions) return false

  return permissions.permissions.some((p) => p.resource === resource)
}

/**
 * Checks if a feature flag is enabled for the current user.
 * Uses cached feature flags — does NOT make a new RPC call.
 */
export function useHasFeature(featureKey: string): boolean {
  const { data: permissions } = useUserPermissions()

  if (!permissions?.features) return false

  return permissions.features[featureKey] === true
}

/** The current user's role name (e.g. 'admin' | 'manager' | 'user'), or null. */
export function useUserRole(): string | null {
  const { data } = useUserPermissions()
  return data?.role?.name ?? null
}

/**
 * Only managers and admins may activate/deactivate practices, providers and
 * affiliations. Regular users can view but not toggle status.
 */
export function useCanToggleStatus(): boolean {
  const role = useUserRole()
  return role === 'admin' || role === 'manager'
}

/**
 * Admin-only surfaces (#1170). Reads the RBAC role, not the deprecated
 * `users.role` ENUM.
 *
 * Returns false while permissions load, so an admin-only surface never flashes
 * into view before the role is known.
 */
export function useIsAdmin(): boolean {
  return useUserRole() === 'admin'
}

/**
 * PermissionGate component
 * Renders children only if the user has the specified permission.
 */
interface PermissionGateProps {
  resource: string
  action: string
  fallback?: ReactNode
  children: ReactNode
}

export function PermissionGate({ resource, action, fallback = null, children }: PermissionGateProps) {
  const hasPermission = useHasPermission(resource, action)
  return hasPermission
    ? createElement(Fragment, null, children)
    : createElement(Fragment, null, fallback)
}

/**
 * Route-level permission guard.
 * Renders children when the user has the specified permission.
 * Redirects to /dashboard while loading returns null (no flicker).
 */
interface PermissionRouteGuardProps {
  resource: string
  action: string
  children: ReactNode
}

export function PermissionRouteGuard({ resource, action, children }: PermissionRouteGuardProps) {
  const canAccess = useHasPermission(resource, action)
  const { isLoading } = useUserPermissions()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !canAccess) {
      navigate({ to: '/dashboard' })
    }
  }, [isLoading, canAccess, navigate])

  if (isLoading || !canAccess) return null

  return createElement(Fragment, null, children)
}
